import React, { Fragment } from 'react'
import { findDOMNode } from 'react-dom'
import styles from './PhotoClusters.scss'
import { InteractiveMarker } from 'common/Map/InteractiveMarker'
import * as upgma from 'lib/UPGMACluster'
import _ from 'lodash'
import { getConsole } from 'lib/getConsole'
import { showGallery } from 'features/gallery/showGallery'
import { Image } from 'common/Image'
import { pointDistanceObject, pointAverageObject, getNearestPoint, validLatLng } from 'lib/geoUtils'
const console = getConsole('common', 'PhotoClusters')

const MAX_PHOTOS_IN_POPUP = 5

// optimization: using interpolation search could give us a big bump here
// since we know that timestamp increases in a semi-regular fashion
const getClosestPointByTimestamp = (points, timestamp) => {
  if (points.length === 1) {
    return points[0]
  }
  const pivot = Math.floor(points.length / 2)
  if (points[pivot].time > timestamp) {
    return getClosestPointByTimestamp(points.slice(0, pivot), timestamp)
  } else {
    return getClosestPointByTimestamp(points.slice(pivot), timestamp)
  }
}

// Base threshold off trip distance divided by 30.
// lower threshold at higher zoom levels to get more precise photo placement
// (values are experimentally determined)
// Safety fallback to 4km if NaN.
const zoomClusterThreshold = (distance, zoom) => {
  if (zoom >= 17) { return 1 }
  if (zoom >= 16) { return 50 }
  if (zoom >= 15) { return 100 }
  if (zoom >= 14) { return 140 }
  if (zoom >= 13) { return 175 }
  if (zoom >= 12) { return 420 }
  return distance / 30 || 4000
}

const cluster = (distance, zoom, latLngs) => upgma.cluster(
  latLngs,
  zoomClusterThreshold(distance, zoom),
  { getDistanceFn: pointDistanceObject, getAvgFn: pointAverageObject }
)

// TRIP photo clustering implmentation. strategy is as follows:
// - for all photos with latlngs, but no timestamps, map to their latlngs
// - for all photos with no latlngs,
//   - find all photos with capturedAt values that fall in the window of
//     (trip start - thirty minutes) to (trip end + thirty minutes)
//   - for photos capturedAt (trip start - thiry minutes) to (trip start), map to first trip trackPoint
//   - for photos capturedAt (trip end) to (trip end + thirty minutes), map to last trip trackPoint
//   - otherwise infer latlng from capturedAt via getClosestPointByTimestamp
// - do not render photos with no latlngs and null capturedAt values or values outside the above window
// see https://github.com/ridewithgps/ridewithgps/issues/8833 for context
const THIRTY_MINUTES = 30 * 60 * 1000
const clusterPhotosTrip = _.memoize(zoom => _.memoize(trip => _clusterPhotosTrip(zoom, trip), trip => trip && `${trip.id}-${trip.photos.length}`))
const _clusterPhotosTrip = (zoom, trip) => {
  console.log(`clusterPhotos cache miss for trip.id=${trip.id}`)
  if (trip.photos.length === 0) {
    return []
  }

  const tripStart = +Date.parse(trip.departedAt)
  const tripStartPadded = tripStart - THIRTY_MINUTES
  const tripEnd = tripStart + trip.metrics.duration * 1000
  const tripEndPadded = tripEnd + THIRTY_MINUTES
  const { trackPoints } = trip
  const firstTP = trackPoints[0]
  const lastTP = trackPoints[trackPoints.length - 1]

  // as with routes, these will will duck type as lagLng objects
  const validPhotos = []
  const latLngs = trip.photos
    .filter(validLatLng)
    .map(p => {
      let latLngLike
      if (p.lat && p.lng) {
        latLngLike = p
        validPhotos.push(p)
      } else if (p.capturedAt) {
        const timestamp = +Date.parse(p.capturedAt)
        if (!timestamp) { return }
        if (!((timestamp > tripStartPadded) && (timestamp < tripEndPadded))) { return }
        if (timestamp < tripStart) { latLngLike = firstTP }
        if (timestamp < tripEnd) {
          const tripTime = Math.floor(Date.parse(p.capturedAt) / 1000)
          latLngLike = getClosestPointByTimestamp(trackPoints, tripTime)
        }
        if (!latLngLike) latLngLike = lastTP
        validPhotos.push(p)
      }
      return latLngLike
    }).filter(p => p)

  const nearestPoints = latLngs.map(p => getNearestPoint(trip, { lat: p.lat, lng: p.lng }))
  return cluster(trip.distance, zoom, latLngs).map(c => {
    const photos = c.map(i => validPhotos[i])
    // We don't want to use pointAverageObject for position here
    // because unlike in the route case where latLngs are gps-derived
    // and will typically cluster along the route, the points here are
    // just bits of the route line and averaging the points along a
    // (typically non-straight) route segment will cause the photo indicator
    // to float off to the side of the route line.
    //
    // Instead we just use the first point, which is a pretty good
    // approximation and saves a lot of time (we'd otherwise need to sort points
    // by distance for each cluster to find the median)
    //
    // the exception here is that if a cluster has only one photo in it, then
    // we place the cluster at the photo latlng rather than on the track line
    const firstPointIndex = c[0]
    const position = (photos.length === 1 && photos[0].lat && photos[0].lng)
      ? { lat: photos[0].lat, lng: photos[0].lng }
      // null in nearestPoints array means that cluster is outside our
      // getNearestPoint threshold, so just put this cluster at the first photo
      : nearestPoints[firstPointIndex] ?? { lat: photos[0].lat, lng: photos[0].lng }
    return { photos, position }
  })
}

// ROUTE photo clustering implementation. Since we don't have timestamps but
// we do have lat/lng data (in theory), run a geographically aware UPGMA
// clustering where we swap in functions to compute distance between lat/lng
// values and average those values.
//
// A possible quick-n-easy speedup here would be to pre-convert all the
// lat/lngs to radians *before* running the computation, which would
// eliminate a lot of converting back and forth in geoUtils.
const clusterPhotosRoute = _.memoize(zoom => _.memoize(route => _clusterPhotosRoute(zoom, route), route => route && `${route.id}-${route.photos.length}`))
const _clusterPhotosRoute = (zoom, route) => {
  console.log(`clusterPhotos cache miss for route.id=${route.id}`)
  if (route.photos.length === 0) {
    return []
  }
  // grab photos with a valid location
  const hasLocation = p => p && p.lat && p.lng
  const validPhotos = route.photos.filter(hasLocation)

  // in this case, we don't need a separate array for cluster data
  // since photos already duck-type as latLng objects
  const latLngs = validPhotos

  return cluster(route.distance, zoom, latLngs).map(c => {
    const photos = c.map(i => validPhotos[i])
    const position = pointAverageObject(photos) // average photo positions to get location
    return { photos, position }
  })
}

// Render a layer of photo clusters for a give trip or route
export class PhotoClusters extends React.PureComponent {
  render () {
    const { troute, clusterStrategy, zoom } = this.props
    const clusters = clusterStrategy === 'trip'
      ? clusterPhotosTrip(zoom)(troute)
      : clusterPhotosRoute(zoom)(troute)
    return (
      <Fragment>
        {clusters.map(({ photos, position }) =>
          <PhotoCluster key={photos[0].id}
            position={position}
            photos={photos}
            openGallery={(activePhotoId) => showGallery({ photos, activePhotoId })} />)}
      </Fragment>
    )
  }
}

// Render a single cluster of photos, with hover preview
export const PhotoCluster = ({ photos, position, openGallery }) => {
  const hoveredPopup = (
    <div className={styles.smallPopup}>
      {photos.slice(0, MAX_PHOTOS_IN_POPUP).map(p => <Image key={p.id}
        id={p.id}
        height={40}
        width={40} />)}
      {photos.length > MAX_PHOTOS_IN_POPUP &&
        <div
          className={styles.more}
          ref={component => component && findDOMNode(component).addEventListener('click', () => openGallery(photos[MAX_PHOTOS_IN_POPUP].id))}>
          <span>+{photos.length - MAX_PHOTOS_IN_POPUP}</span>
        </div>}
    </div>
  )

  const focusedPopup = (
    <div className={styles.bigPopup}>
      {photos.slice(0, MAX_PHOTOS_IN_POPUP).map(p => <Image key={p.id}
        id={p.id}
        // we have to use native click events because
        // of map stopPropagation shenanigans
        ref={component => component && findDOMNode(component).addEventListener('click', () => openGallery(p.id))}
        height={100}
        width={100} />)}
      {photos.length > MAX_PHOTOS_IN_POPUP &&
        <a
          className={styles.more}
          ref={component => component && findDOMNode(component).addEventListener('click', () => openGallery(photos[MAX_PHOTOS_IN_POPUP].id))}>
          <span>+{photos.length - MAX_PHOTOS_IN_POPUP}</span>
        </a>}
    </div>
  )

  return (
    <InteractiveMarker
      position={position}
      icon='photo'
      id={`cluster-${position.lat}-${position.lng}`}
      hoveredPopup={hoveredPopup}
      focusedPopup={focusedPopup}
      popupOffset={[0, -35]} />
  )
}
